import type { TabularConfigBuildJobResult } from '@axilon-apps/domains-ontology-ui-state'
import type { Status } from '../../components/primitives/types'
import { statusToLabel, statusToPill, type IngestJobState } from '../IngestPage/util/jobStatusMap'

export interface TabularJobPill {
  status: Status
  label: string
}

/**
 * A config build's pill follows the builder phase when the job has recorded
 * one, since the generic job state only says "running" or "review needed".
 * A user stop wins over the phase: the runtime records a stopped build as
 * phase `failed`, which would otherwise read as a builder failure.
 */
export function configBuildPill(
  state: IngestJobState,
  phase?: TabularConfigBuildJobResult['phase']
): TabularJobPill {
  if (state === 'cancelling' || state === 'cancelled') {
    return { status: statusToPill[state], label: statusToLabel[state] }
  }
  switch (phase) {
    case 'awaiting_review':
      return { status: 'warn', label: 'draft ready' }
    case 'approving':
      return { status: 'info', label: 'saving' }
    case 'approved':
      return { status: 'run', label: 'approved' }
    case 'rejected':
      return { status: 'idle', label: 'rejected' }
    case 'failed':
      return { status: 'down', label: 'failed' }
    case 'verifying':
      return { status: 'info', label: 'verifying' }
    case 'building':
      return { status: 'info', label: 'building' }
    default:
      return { status: statusToPill[state], label: statusToLabel[state] }
  }
}

/** `committing`: a held run's reviewed rows are being written. */
export function tabularJobPill(
  state: IngestJobState,
  acknowledged: boolean,
  parserKind: 'script' | 'agent',
  committing = false
): TabularJobPill {
  if (state === 'error' && acknowledged) return { status: 'idle', label: 'failed · ignored' }
  if (state === 'processing' && committing) return { status: 'info', label: 'committing' }
  if (state === 'processing' && parserKind === 'agent') {
    return { status: statusToPill[state], label: 'extracting (agent)' }
  }
  return { status: statusToPill[state], label: statusToLabel[state] }
}
